import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import type { FirebaseOptions } from 'firebase/app';
import type { Analytics } from 'firebase/analytics';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { LanguageService } from '@shared/partner-ui/services/language.service';

type AnalyticsParams = Record<string, string | number | boolean>;

type ClickOptions = {
  eventName?: string;
  location?: string;
  destination?: string;
  element?: string;
  context?: string;
};

type QueuedEvent = {
  name: string;
  params: AnalyticsParams;
};

type FirebaseAppModule = typeof import('firebase/app');
type FirebaseAnalyticsModule = typeof import('firebase/analytics');

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private readonly router = inject(Router);
  private readonly language = inject(LanguageService);
  private readonly auth = inject(AuthService);
  private analytics: Analytics | null = null;
  private analyticsDisabled = false;
  private initializing = false;
  private initializationScheduled = false;
  private analyticsModule?: FirebaseAnalyticsModule;
  private firebaseModulesPromise?: Promise<{
    appModule: FirebaseAppModule;
    analyticsModule: FirebaseAnalyticsModule;
  }>;
  private readonly pendingEvents: QueuedEvent[] = [];

  constructor() {
    if (!environment.firebaseConfig) {
      this.analyticsDisabled = true;
      return;
    }

    this.bindRouter();
    this.scheduleInitialization();
  }

  trackClick(label: string, options: ClickOptions = {}): void {
    if (!label) {
      return;
    }

    this.logEvent(options.eventName ?? 'button_click', {
      label,
      location: options.location,
      destination: options.destination,
      element: options.element,
      context: options.context
    });
  }

  private bindRouter(): void {
    this.trackPageView(this.router.url);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.trackPageView(event.urlAfterRedirects));
  }

  private trackPageView(url: string): void {
    this.logEvent('page_view', {
      page_path: url,
      page_location: typeof location !== 'undefined' ? location.href : undefined,
      page_title: typeof document !== 'undefined' ? document.title : undefined,
      page_referrer: typeof document !== 'undefined' ? document.referrer : undefined
    });
  }

  private async initialize(): Promise<void> {
    if (this.analytics || this.analyticsDisabled || this.initializing) {
      return;
    }

    if (typeof window === 'undefined') {
      this.analyticsDisabled = true;
      this.pendingEvents.length = 0;
      return;
    }

    this.initializing = true;
    try {
      const { appModule, analyticsModule } = await this.loadFirebaseModules();
      if (!(await analyticsModule.isSupported())) {
        this.analyticsDisabled = true;
        this.pendingEvents.length = 0;
        return;
      }

      this.ensureFirebaseApp(appModule);
      this.analyticsModule = analyticsModule;
      this.analytics = analyticsModule.getAnalytics();
      this.flushPendingEvents(analyticsModule);
    } catch {
      this.analyticsDisabled = true;
      this.pendingEvents.length = 0;
    } finally {
      this.initializing = false;
    }
  }

  private scheduleInitialization(): void {
    if (this.analytics || this.analyticsDisabled || this.initializing || this.initializationScheduled) {
      return;
    }

    this.initializationScheduled = true;

    const schedule = () => {
      this.initializationScheduled = false;
      void this.initialize();
    };

    if (typeof window === 'undefined') {
      schedule();
      return;
    }

    const callbackWindow = window as Window & {
      requestIdleCallback?: (cb: () => void, options?: { timeout: number }) => number;
    };

    if (typeof callbackWindow.requestIdleCallback === 'function') {
      callbackWindow.requestIdleCallback(schedule, { timeout: 2500 });
      return;
    }

    window.setTimeout(schedule, 800);
  }

  private async loadFirebaseModules(): Promise<{
    appModule: FirebaseAppModule;
    analyticsModule: FirebaseAnalyticsModule;
  }> {
    if (!this.firebaseModulesPromise) {
      this.firebaseModulesPromise = Promise.all([
        import('firebase/app'),
        import('firebase/analytics')
      ]).then(([appModule, analyticsModule]) => ({
        appModule,
        analyticsModule
      }));
    }

    return this.firebaseModulesPromise;
  }

  private ensureFirebaseApp(appModule: FirebaseAppModule): void {
    if (!environment.firebaseConfig) {
      return;
    }
    if (!appModule.getApps().length) {
      appModule.initializeApp(environment.firebaseConfig as FirebaseOptions);
    }
  }

  private logEvent(name: string, params: Record<string, unknown>): void {
    if (this.analyticsDisabled) {
      return;
    }

    const enriched = this.enrichParams(params);
    if (!this.analytics) {
      this.queueEvent(name, enriched);
      this.scheduleInitialization();
      return;
    }

    if (!this.analyticsModule) {
      this.scheduleInitialization();
      return;
    }

    this.analyticsModule.logEvent(this.analytics, name, enriched);
  }

  private enrichParams(params: Record<string, unknown>): AnalyticsParams {
    const authState = this.auth.isHydrated()
      ? (this.auth.isAuthenticated() ? 'authenticated' : 'anonymous')
      : 'loading';
    return this.sanitizeParams({
      ...params,
      language: this.language.currentLanguage,
      auth_state: authState
    });
  }

  private sanitizeParams(params: Record<string, unknown>): AnalyticsParams {
    return Object.entries(params).reduce<AnalyticsParams>((acc, [key, value]) => {
      if (value === undefined || value === null || value === '') {
        return acc;
      }
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        acc[key] = value;
      } else {
        acc[key] = String(value);
      }
      return acc;
    }, {});
  }

  private queueEvent(name: string, params: AnalyticsParams): void {
    this.pendingEvents.push({ name, params });
    if (this.pendingEvents.length > 25) {
      this.pendingEvents.shift();
    }
  }

  private flushPendingEvents(analyticsModule: FirebaseAnalyticsModule): void {
    if (!this.analytics) {
      return;
    }
    for (const event of this.pendingEvents) {
      analyticsModule.logEvent(this.analytics, event.name, event.params);
    }
    this.pendingEvents.length = 0;
  }
}
